import React from 'react';

import PropTypes from 'prop-types';

import { RenderIconType } from '@adsk/alloy-react-icon';
import theme, {
  stylePropType,
  StylableComponent,
} from '@adsk/alloy-react-theme';

import TypographyWidthHolder from './TypographyWidthHolder';

type TabTitleProps = StylableComponent<HTMLButtonElement> & {
  disabled?: boolean;
  active?: boolean;
  renderIcon?: RenderIconType;
  href?: string;
  'data-testid'?: string;
};

/**
 * Tab title - the clickable item used to switch between tabs.
 */
const TabTitle = React.forwardRef<HTMLButtonElement, TabTitleProps>(
  (
    {
      style,
      className,
      disabled,
      children,
      active,
      renderIcon,
      onClick,
      ...props
    },
    ref,
  ) => {
    const labelAndIcon = children && renderIcon;
    return (
      <button
        {...props}
        ref={ref}
        className={className}
        disabled={disabled}
        type="button"
        role="tab"
        css={[
          {
            position: 'relative',
            display: 'flex',
            justifyContent: labelAndIcon ? 'space-between' : 'center',
            alignItems: 'center',
            margin: `0 ${theme.spacing.S}px`,
            textDecoration: 'none',
            minWidth: 48,

            '&:first-child': {
              marginLeft: 0,
            },

            '&:last-child': {
              marginRight: 0,
            },

            '&::-moz-focus-inner': {
              border: 0,
            },

            background: 'none',
            outline: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: `0 ${theme.spacing.XXS}px 6px`,
            color: theme.tokens.colors.text.primary.value,
            ...theme.typography.heading4,

            '&:focus': {
              '&:before': {
                content: "' '",
                position: 'absolute',
                bottom: '-3px',
                right: 0,
                left: 0,
                borderBottom: `2px solid ${theme.colors.focusHalo}`,
              },
            },

            '&:active': {
              ...(!disabled && theme.typography.heading4),
            },

            '&:active:focus': {
              '&:before': {
                borderBottom: 0,
              },
            },

            ...(!disabled && {
              '&:hover': {
                color: theme.tokens.colors.text.hover.value,
              },
            }),

            ...(active && {
              color: theme.tokens.colors.text.primary.value,
            }),

            ...(!active && {
              fontWeight: 500,
            }),

            ...(!active &&
              disabled && {
                color: theme.tokens.colors.text.inactive.value,
                cursor: 'default',
              }),
          },
          style,
        ]}
        onClick={onClick}
      >
        {renderIcon?.({
          style: {
            ...(children && {
              marginRight: theme.spacing.XS,
            }),
            ...(disabled && {
              color: theme.tokens.colors.icon.inactive.value,
            }),
          },
        })}
        <TypographyWidthHolder typographyStyle={theme.typography.heading4}>
          {children}
        </TypographyWidthHolder>
      </button>
    );
  },
);

TabTitle.displayName = 'TabTitle';

TabTitle.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** whether this tab is disables */
  disabled: PropTypes.bool,
  /** whether this tab is currently active */
  active: PropTypes.bool,
  /** The content of the tab title */
  children: PropTypes.any,
  /** Render the icon */
  renderIcon: PropTypes.func,
  /** Handles title click */
  onClick: PropTypes.func,
};

export default TabTitle;
